import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Empty, List, Row, Skeleton, Space, Statistic, Tag, Typography } from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import SubmissionStatus from '../components/SubmissionStatus';
import { formatDateTime } from '../utils/recordView';

const { Title, Text, Paragraph } = Typography;

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const version = useRef(0);
  const push = hasPermission('pushRecords', 'read');
  const sites = hasPermission('sites', 'read');
  const groups = hasPermission('groupConfig', 'read');
  const robots = user?.role === 'super_admin' && hasPermission('robotSettings', 'read');
  const messages = hasPermission('wechatRecords', 'read');
  const load = useCallback(async () => {
    const id = ++version.current;
    setLoading(true);
    const requests = [
      push && ['overview', '/push-records/stats/workbench', '报名概况'],
      sites && ['sites', '/sites/stats/summary', '岗位查询'],
      groups && ['groups', '/group-config/stats/summary', '报名处理规则'],
      robots && ['robots', '/robot-settings/stats/summary', '机器人状态']
    ].filter(Boolean);
    const results = await Promise.allSettled(requests.map(async ([key, url]) => ({ key, value: (await api.get(url)).data })));
    if (id !== version.current) return;
    const updates = {}, failed = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') updates[result.value.key] = result.value.value;
      else failed.push(`${requests[index][2]}：${result.reason.response?.data?.message || '暂不可用'}`);
    });
    setData(previous => ({ ...previous, ...updates }));
    setErrors(failed);
    setLoading(false);
  }, [push, sites, groups, robots]);
  useEffect(() => { load(); return () => { version.current += 1; }; }, [load]);

  const overview = data.overview;
  const recordLink = values => `/push-records?${new URLSearchParams(values)}`;
  const today = overview?.todayRange || {};
  const metrics = [
    { label: '今日新增报名', value: overview?.today.total, note: '按今日创建的报名记录统计', icon: <FileTextOutlined />, color: 'blue', link: recordLink(today) },
    { label: '今日报名已接收', value: overview?.today.accepted, note: '今日新增中，伙伴通已建档', icon: <CheckCircleOutlined />, color: 'green', link: recordLink({ ...today, submissionStatus: 'accepted' }) },
    { label: '待处理报名', value: overview?.backlog.blocked, note: '全部时间 · 尚未向伙伴通提交', icon: <ClockCircleOutlined />, color: 'amber', link: recordLink({ submissionStatus: 'blocked' }) },
    { label: '接收结果需核对', value: overview?.backlog.unknown, note: '全部时间 · 需要核对伙伴通记录', icon: <ExclamationCircleOutlined />, color: 'purple', link: recordLink({ submissionStatus: 'unknown' }) }
  ];
  const quickLinks = [
    push && { title: '推送记录', description: '查看人员、匹配岗位和接收结果', path: '/push-records' },
    messages && { title: '微信消息', description: '检查消息是否收到、为何未处理', path: '/wechat-records' },
    groups && { title: '报名处理规则', description: '按来源群和触发词控制报名处理', path: '/wechat-records/groups' },
    user?.role === 'super_admin' && { title: '岗位查询设置', description: '配置实时岗位接口和查询 Key', path: '/settings/job-source' }
  ].filter(Boolean);

  return <div>
    <div className="page-header page-header-with-actions">
      <div><Title level={2}>仪表盘</Title><div className="description">{user?.username ? `${user.username}，` : ''}从待办开始，查看报名处理与接入情况。</div></div>
      <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新数据</Button>
    </div>
    {errors.length > 0 && <Alert className="page-alert" type="warning" showIcon message="部分数据加载失败"
      description={<>{errors.map(error => <div key={error}>{error}</div>)}<div>已加载的数据保留上次结果；暂无结果的指标显示为“—”。</div></>} action={<Button size="small" onClick={load}>重试</Button>} />}
    {push && <>
      <Row gutter={[16, 16]} className="stats-cards">
        {metrics.map(metric => <Col xs={12} xl={6} key={metric.label}>
          <button className={`metric-card metric-${metric.color}`} disabled={!overview} onClick={() => navigate(metric.link)}>
            <span className="metric-heading"><span>{metric.label}</span><span className="metric-icon">{metric.icon}</span></span>
            {loading && !overview ? <Skeleton.Input active size="small" /> : <Statistic value={metric.value ?? '—'} />}
            <span className="metric-note">{metric.note}</span><span className="metric-link">查看记录 <ArrowRightOutlined /></span>
          </button>
        </Col>)}
      </Row>
      <Row gutter={[20, 20]}>
        <Col xs={24} xl={15}><Card title="需要关注的报名" extra={<Button type="link" onClick={() => navigate('/push-records?submissionStatus=blocked')}>查看待处理 <ArrowRightOutlined /></Button>}>
          {loading && !overview ? <Skeleton active /> : !overview ? <Empty description="报名数据暂不可用" image={Empty.PRESENTED_IMAGE_SIMPLE} /> :
            <List dataSource={overview.tasks} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无未提交或接收结果待确认的报名" /> }}
              renderItem={item => <List.Item className="task-row" actions={[<Button key="detail" onClick={() => navigate(`/push-records?detail=${encodeURIComponent(item._id)}`)}>查看</Button>]}>
                <List.Item.Meta title={<Space wrap><Text strong>{item.name}</Text><SubmissionStatus status={item.submissionStatus} /></Space>}
                  description={<><div className="task-reason">{item.submissionMessage || '打开详情查看处理原因'}</div><Text type="secondary">{[item.site, item.city, item.senderGroupName].filter(Boolean).join(' · ')}</Text></>} />
              </List.Item>} />}
          {overview && <div className="card-footer-note">处理中 / 待核对：{overview.backlog.pending} 条 <Button type="link" size="small" onClick={() => navigate('/push-records?submissionStatus=pending')}>查看记录</Button></div>}
        </Card></Col>
        <Col xs={24} xl={9}><Card title="运行概况" className="full-height">
          <Space direction="vertical" size={20} className="full-width">
            <Paragraph type="secondary">自动处理链路：微信消息 → 识别 → 岗位匹配 → 伙伴通接收。</Paragraph>
            {sites && <div className="health-row"><span>实时岗位</span><Text>{data.sites ? `${data.sites.total} 个` : '暂不可用'}</Text></div>}
            {groups && <div className="health-row"><span>启用的报名处理规则</span><Text>{data.groups ? `${data.groups.collectable ?? '—'} / ${data.groups.total} 条` : '暂不可用'}</Text></div>}
            {robots && <div className="health-row"><span>设备在线</span><Text>{data.robots ? `${data.robots.statusStats?.online ?? '—'} / ${data.robots.total} 个` : '暂不可用'}</Text></div>}
            <Alert type="info" showIcon message="已接收表示伙伴通已受理、建档" description="后续群发送由伙伴通处理。接收结果待确认时，请先核对远端记录。" />
          </Space>
        </Card></Col>
      </Row>
      <Card className="section-card" title="最近 7 天报名趋势" extra={<Tag>按报名创建日期 · 北京时间</Tag>}>
        <Paragraph type="secondary">“已接收”统计各日新增报名中当前已被伙伴通接收的数量。</Paragraph>
        {overview?.trend?.length ? <div className="trend-chart"><ResponsiveContainer width="100%" height={270}>
          <LineChart data={overview.trend} margin={{ top: 12, right: 16, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf0f5" />
            <XAxis dataKey="date" tickFormatter={date => date.slice(5)} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
            <Tooltip /><Legend />
            <Line type="monotone" dataKey="count" name="新增报名" stroke="#2563eb" strokeWidth={2.5} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="acceptedCount" name="已接收" stroke="#16a34a" strokeWidth={2.5} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer></div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无趋势数据" />}
      </Card>
      {overview && <Paragraph type="secondary" className="updated-at">报名概况更新于 {formatDateTime(overview.generatedAt)}</Paragraph>}
    </>}
    {quickLinks.length > 0 && <Card className="section-card" title="常用入口"><Row gutter={[16, 16]}>
      {quickLinks.map(link => <Col xs={24} sm={12} xl={6} key={link.path}><button className="quick-link" onClick={() => navigate(link.path)}>
        <span className="quick-link-title">{link.title}<ArrowRightOutlined /></span><span>{link.description}</span>
      </button></Col>)}
    </Row></Card>}
    {!push && <Alert className="page-alert" type="info" showIcon message="当前账号没有报名记录查看权限" description="可使用已授权的功能；需要查看报名概况时，请联系管理员。" />}
  </div>;
}
